import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import NotificationSettings from '../components/NotificationSettings';
import WhatsAppTemplates from '../components/WhatsAppTemplates';
import GoogleSheetsSettings from '../components/GoogleSheetsSettings';
import OfflinePaymentSettings from '../components/OfflinePaymentSettings';
import EventVendorsSettings from '../components/EventVendorsSettings';
import RazorpayEventVendorsSettings from '../components/RazorpayEventVendorsSettings';
import CouponCodes from '../components/CouponCodes';
import { ArrowLeft, Bell, Sheet, Settings, Loader2, Banknote, Users, MessageCircle, Ticket } from 'lucide-react';
import { toast } from 'sonner';

const CampaignSettings = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('notifications');

  // Kiosk volunteers cannot access event settings
  useEffect(() => {
    if (user?.role === 'kiosk_volunteer') {
      navigate('/dashboard');
      return;
    }
    fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, user?.role]);

  const fetchCampaign = async () => {
    try {
      const response = await api.get(`/campaigns/${campaignId}`);
      setCampaign(response.data);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const tabs = [
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { id: 'sheets', label: 'Google Sheets', icon: Sheet },
    { id: 'offline', label: 'Offline Payments', icon: Banknote },
    { id: 'coupons', label: 'Coupons', icon: Ticket },
    { id: 'vendors', label: 'Vendors', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/campaigns/${campaignId}`)}
              className="hover:bg-slate-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-primary" />
                <h1 className="text-xl font-outfit font-bold text-slate-900">Event Settings</h1>
              </div>
              <p className="text-sm font-jakarta text-slate-600 mt-0.5">{campaign?.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm mb-6 border border-slate-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'notifications' && (
          <NotificationSettings campaignId={campaignId} campaignName={campaign?.name} />
        )}
        
        {activeTab === 'whatsapp' && (
          <WhatsAppTemplates campaignId={campaignId} campaignName={campaign?.name} />
        )}

        {activeTab === 'sheets' && (
          <GoogleSheetsSettings campaignId={campaignId} campaignName={campaign?.name} />
        )}
        
        {activeTab === 'offline' && (
          <OfflinePaymentSettings campaignId={campaignId} campaignName={campaign?.name} />
        )}

        {activeTab === 'coupons' && (
          <CouponCodes campaignId={campaignId} campaignName={campaign?.name} />
        )}

        {activeTab === 'vendors' && campaign?.payment_gateway === 'cashfree' && (
          <EventVendorsSettings campaignId={campaignId} campaignName={campaign?.name} />
        )}
        {activeTab === 'vendors' && campaign?.payment_gateway === 'razorpay' && (
          <RazorpayEventVendorsSettings campaignId={campaignId} campaignName={campaign?.name} />
        )}
      </div>
    </div>
  );
};

export default CampaignSettings;
